// ABOUTME: Constants and configuration for the multi-file upload component
// ABOUTME: Centralized configuration for file validation, UI text, and limits

export const FILE_UPLOAD_CONSTANTS = {
  // File size limits
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  MIN_FILE_SIZE: 1, // 1 byte

  // File count limits
  DEFAULT_MAX_FILES: 20,
  MIN_FILES: 0,

  // Accepted file types
  DEFAULT_ACCEPTED_TYPES: ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'] as string[],
  IMAGE_TYPES: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'],
  DOCUMENT_TYPES: ['.pdf', '.doc', '.docx', '.txt'],

  // UI Text
  DEFAULT_LABEL: 'Upload files',
  CHOOSE_FILES_LABEL: 'Choose files',
  FILES_UPLOADING: 'Uploading files...',
  DRAG_DROP_TEXT: 'or drag & drop your files here',
  DROP_FILES_TEXT: 'Drop your files here to upload them',
  OPTIONAL_TEXT: '(optional)',

  // Error messages
  ERRORS: {
    FILE_TOO_LARGE: (maxSize: number) => `The file size must not exceed ${maxSize / (1024 * 1024)} MB`,
    FILE_TOO_SMALL: 'File is empty',
    INVALID_TYPE: (acceptedTypes: string[]) => `File type not accepted. Accepted types: ${acceptedTypes.join(', ')}`,
    MAX_FILES_EXCEEDED: (maxFiles: number) => `Maximum ${maxFiles} files allowed`,
    UPLOAD_FAILED: 'Upload failed. Please try again.',
    NETWORK_ERROR: 'Network error. Please check your connection.',
    GENERIC_ERROR: 'An error occurred. Please try again.',
    UPLOAD_IN_PROGRESS: 'Please wait until all files have finished uploading',
    MULTIPLE_FAILURES: 'Some files failed to upload. Please review the errors and try uploading again.',
    RETRY_EXCEEDED: (maxRetries: number) => `Upload failed after ${maxRetries} attempts`,
  },

  // Animation durations (ms)
  ANIMATION: {
    DRAG_ENTER: 200,
    DRAG_LEAVE: 200,
    DRAG_OVERLAY_FADE: 200,
    CLOUD_BOUNCE: 600,
    BORDER_PULSE: 1500,
    PROGRESS_UPDATE: 100,
    COMPLETION_SPEED_UP: 50,
  },

  // Accessibility
  ARIA_LABELS: {
    DROPZONE: 'File upload drop zone. Press Enter to open file selector or drag files here',
    DROPZONE_ACTIVE: 'Drop zone active - release files to upload',
    FILE_INPUT: 'Select files to upload',
    DELETE_FILE: (fileName: string) => `Delete ${fileName}`,
    FILE_STATUS: (fileName: string, status: string) => `${fileName} - ${status}`,
    PROGRESS: (fileName: string, progress: number) => `${fileName} upload progress: ${progress}%`,
    DRAG_OVERLAY: 'Drag overlay visible - drop files to upload',
  },
} as const;

// File status types with better type safety
export const FILE_STATUS = {
  READY: 'ready',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
  CANCELLED: 'cancelled',
} as const;

export type FileStatus = (typeof FILE_STATUS)[keyof typeof FILE_STATUS];

// MIME type mappings for better validation
export const MIME_TYPE_MAP: Record<string, string[]> = {
  '.pdf': ['application/pdf'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.png': ['image/png'],
  '.gif': ['image/gif'],
  '.bmp': ['image/bmp'],
  '.webp': ['image/webp'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.txt': ['text/plain'],
};
